import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertTriangle, MessageSquare, X, Trash2, Search } from 'lucide-react';
import { uploadDocuments, getUnmatchedDocuments, getAllDocuments, assignDocument, deleteDocument, setDocumentNote, DOCUMENT_CATEGORIES } from '../../api';

// Lives inside the "Upload zone" tab (see GPSUpload.js), not a page of its own — that's the tab
// the user already knows as "Upload zone", so the PDF flow lands there rather than getting a new
// nav entry. requesterRole is hardcoded 'staff' rather than threaded down from a user prop, same
// shortcut the SSG pages already take (see PageChrome.js/SsgStandings.js) — this tab is itself
// gated by allowedTabs, so anyone who can see it already satisfies the backend's staff/admin check.
export default function DocumentUploadSection({ players }) {
  const [category, setCategory] = useState('Other');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null); // last upload's per-file match results
  const [error, setError] = useState('');
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [allDocs, setAllDocs] = useState([]);
  const [allDocsLoading, setAllDocsLoading] = useState(true);
  const [docSearch, setDocSearch] = useState('');
  const fileInputRef = useRef(null);

  // Notes — a general note applies to every document in the current results batch unless a
  // specific document has its own note (set via the popup below), which wins for that one.
  // Neither is sent to the server until "Send all" — the popup's "Save" only stores it locally.
  const [generalNote, setGeneralNote] = useState('');
  const [perDocNotes, setPerDocNotes] = useState({});
  const [notePopupFor, setNotePopupFor] = useState(null);
  const [sendingNotes, setSendingNotes] = useState(false);
  const [notesSent, setNotesSent] = useState(false);

  const loadQueue = () => {
    setQueueLoading(true);
    getUnmatchedDocuments('staff')
      .then(rows => setQueue(rows || []))
      .catch(err => console.error('Failed to load unmatched documents:', err))
      .finally(() => setQueueLoading(false));
  };
  const loadAllDocs = () => {
    setAllDocsLoading(true);
    getAllDocuments('staff')
      .then(rows => setAllDocs(rows || []))
      .catch(err => console.error('Failed to load documents:', err))
      .finally(() => setAllDocsLoading(false));
  };
  useEffect(() => { loadQueue(); loadAllDocs(); }, []);

  const handleFiles = async fileList => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const nonPdf = files.filter(f => f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name));
    setError(nonPdf.length ? `Only PDF files are accepted — skipped: ${nonPdf.map(f => f.name).join(', ')}` : '');
    const pdfFiles = files.filter(f => !nonPdf.includes(f));
    if (!pdfFiles.length) return;

    setUploading(true);
    setResults(null);
    setGeneralNote('');
    setPerDocNotes({});
    setNotesSent(false);
    try {
      const uploaded = await uploadDocuments(pdfFiles, { category, requesterRole: 'staff' });
      setResults(uploaded);
      loadQueue();
      loadAllDocs();
    } catch (err) {
      console.error('Failed to upload documents:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleAssign = async (docId, playerId) => {
    if (!playerId) return;
    try {
      await assignDocument(docId, playerId, 'staff');
      loadQueue();
      loadAllDocs();
    } catch (err) {
      console.error('Failed to assign document:', err);
      alert('Failed to assign document. Please try again.');
    }
  };

  const handleDeleteDoc = async doc => {
    if (!window.confirm(`Delete "${doc.original_filename}"? This cannot be undone.`)) return;
    try {
      await deleteDocument(doc.id, 'staff');
      setResults(prev => prev ? prev.filter(r => r.id !== doc.id) : prev);
      loadQueue();
      loadAllDocs();
    } catch (err) {
      console.error('Failed to delete document:', err);
      alert('Failed to delete document. Please try again.');
    }
  };

  const canSendNotes = !!generalNote.trim() || Object.values(perDocNotes).some(n => n?.trim());
  const handleSendAll = async () => {
    setSendingNotes(true);
    try {
      const updates = (results || [])
        .map(r => ({ id: r.id, note: (perDocNotes[r.id] ?? generalNote).trim() }))
        .filter(u => u.note);
      await Promise.all(updates.map(u => setDocumentNote(u.id, u.note, 'staff')));
      setNotesSent(true);
    } catch (err) {
      console.error('Failed to send notes:', err);
      alert('Failed to send notes. Please try again.');
    } finally {
      setSendingNotes(false);
    }
  };

  return (
    <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)', marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <FileText size={18} color="#3b82f6" />
        <h2 style={{ fontSize: 16, fontWeight: 700 }}>Documents</h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        Upload PDFs — the player is detected automatically from the filename (e.g. "John Smith.pdf", "Smith_John.pdf").
        Files that can't be matched land in the queue below for manual assignment.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Category</label>
        <select value={category} onChange={e => setCategory(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
          {DOCUMENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#3b82f6' : 'var(--border-strong)'}`,
          borderRadius: 10, padding: '28px 16px', textAlign: 'center', cursor: 'pointer',
          background: dragOver ? 'rgba(59,130,246,0.06)' : 'var(--bg-app)', transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        <Upload size={22} color="var(--text-muted)" />
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>
          {uploading ? 'Uploading…' : 'Drag PDFs here, or click to browse'}
        </div>
        <input ref={fileInputRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }}
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {error && <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 13 }}>{error}</div>}

      {results && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {results.map(r => {
              const hasNote = !!perDocNotes[r.id]?.trim();
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '8px 12px', borderRadius: 8, background: 'var(--bg-app)' }}>
                  {r.match_status === 'auto' ? <CheckCircle size={14} color="#10b981" /> : <AlertTriangle size={14} color="#f59e0b" />}
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.original_filename}</span>
                  <span style={{ color: r.match_status === 'auto' ? '#10b981' : '#f59e0b', fontWeight: 600, flexShrink: 0 }}>
                    {r.match_status === 'auto' ? r.player?.name : (r.candidates?.length ? 'Ambiguous — needs assignment' : 'Needs assignment')}
                  </span>
                  <button onClick={() => setNotePopupFor(r.id)} title={hasNote ? 'Edit note' : 'Add note'}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 8px', borderRadius: 6, border: `1px solid ${hasNote ? '#3b82f6' : 'var(--border-strong)'}`, background: hasNote ? 'rgba(59,130,246,0.1)' : 'none', color: hasNote ? '#3b82f6' : 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
                    <MessageSquare size={12} /> {hasNote ? 'Note ✓' : 'Note'}
                  </button>
                  <button onClick={() => handleDeleteDoc(r)} title="Delete"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Note for everyone — applies to every document above, unless one has its own note
            </label>
            <textarea value={generalNote} onChange={e => setGeneralNote(e.target.value)} rows={3}
              placeholder="e.g. Please review before Friday's session…"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <button disabled={!canSendNotes || sendingNotes} onClick={handleSendAll}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: canSendNotes ? '#3b82f6' : 'var(--border)', color: canSendNotes ? '#fff' : 'var(--text-dimmed)', cursor: canSendNotes && !sendingNotes ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
              {sendingNotes ? 'Sending…' : 'Send all'}
            </button>
            {notesSent && (
              <span style={{ fontSize: 12, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle size={14} /> Notes sent
              </span>
            )}
          </div>
        </div>
      )}

      {notePopupFor != null && (() => {
        const doc = results.find(r => r.id === notePopupFor);
        return (
          <NotePopup
            label={doc?.player?.name || doc?.original_filename}
            initialNote={perDocNotes[notePopupFor] || ''}
            onSave={note => { setPerDocNotes(prev => ({ ...prev, [notePopupFor]: note })); setNotePopupFor(null); }}
            onClose={() => setNotePopupFor(null)}
          />
        );
      })()}

      <div style={{ marginTop: 24, borderTop: '1px solid var(--border-strong)', paddingTop: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Needs assignment{queue.length > 0 ? ` (${queue.length})` : ''}
        </h3>
        {queueLoading ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
        ) : queue.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nothing waiting on a manual assignment.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {queue.map(doc => <QueueRow key={doc.id} doc={doc} players={players} onAssign={handleAssign} onDelete={handleDeleteDoc} />)}
          </div>
        )}
      </div>

      <AllDocumentsList docs={allDocs} loading={allDocsLoading} players={players} search={docSearch} onSearchChange={setDocSearch} onDelete={handleDeleteDoc} />
    </div>
  );
}

// Every document ever uploaded, regardless of match status — the "easy to find and delete
// anything" view, since the results panel above only shows the last batch and the queue only
// shows unmatched ones. Client-side search only (no server-side pagination) — fine at this
// club's scale (dozens to low hundreds of documents over a season), not built to scale further.
function AllDocumentsList({ docs, loading, players, search, onSearchChange, onDelete }) {
  const q = search.trim().toLowerCase();
  const visible = !q ? docs : docs.filter(doc => {
    const player = players.find(p => p.id === doc.player_id);
    return doc.original_filename.toLowerCase().includes(q) || (player?.name || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ marginTop: 24, borderTop: '1px solid var(--border-strong)', paddingTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>
          All documents{docs.length > 0 ? ` (${docs.length})` : ''}
        </h3>
        <div style={{ position: 'relative' }}>
          <Search size={13} color="var(--text-muted)" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search filename or player…"
            style={{ padding: '6px 10px 6px 28px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', minWidth: 220 }} />
        </div>
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {docs.length === 0 ? 'No documents uploaded yet.' : 'No documents match your search.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
          {visible.map(doc => {
            const player = players.find(p => p.id === doc.player_id);
            return (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-app)', borderRadius: 8, fontSize: 13 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_filename}</span>
                <span style={{ color: player ? 'var(--text-secondary)' : '#f59e0b', fontSize: 12, flexShrink: 0 }}>{player ? player.name : 'Unmatched'}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>{doc.category}</span>
                <button onClick={() => onDelete(doc)} title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// "Save" here only writes to local state (perDocNotes) — nothing is persisted until "Send all"
// PUTs it to the server, so this popup can be opened/closed/re-edited freely beforehand.
function NotePopup({ label, initialNote, onSave, onClose }) {
  const [note, setNote] = useState(initialNote || '');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, width: 360, position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={18} /></button>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Note for {label}</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Overrides the general note, for this document only.</p>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={4} autoFocus
          placeholder="Add a note for this document…"
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={() => onSave(note)} style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// Mirrors GPSUpload.js's UnmatchedNameResolver — same "pick a player from a select, one button to
// commit" shape, just assigning a document instead of saving a name alias.
function QueueRow({ doc, players, onAssign, onDelete }) {
  const [playerId, setPlayerId] = useState('');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 12px', background: 'var(--bg-app)', borderRadius: 8 }}>
      <span style={{ fontSize: 13, flex: 1, minWidth: 160 }}>{doc.original_filename}</span>
      <select value={playerId} onChange={e => setPlayerId(e.target.value)}
        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit' }}>
        <option value="">Assign to…</option>
        {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <button disabled={!playerId} onClick={() => onAssign(doc.id, playerId)}
        style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: playerId ? '#3b82f6' : 'var(--border)', color: playerId ? '#fff' : 'var(--text-dimmed)', cursor: playerId ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
        Assign
      </button>
      <button onClick={() => onDelete(doc)} title="Delete"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}
